import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchPastaTypes, fetchSauces, fetchDrinks } from '../services/firestoreService';
import { usePriceCalculator } from '../hooks/usePriceCalculator';
import { trackConfigurationChange } from '../utils/analytics';

const Configurator = () => {
    const { t } = useTranslation();
    const [pastaTypes, setPastaTypes] = useState([]);
    const [sauces, setSauces] = useState([]);
    const [drinks, setDrinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPastaId, setSelectedPastaId] = useState('');
    const [quantity, setQuantity] = useState(100);
    const [selectedSauceId, setSelectedSauceId] = useState('');
    const [selectedDrinkId, setSelectedDrinkId] = useState('');

    // Fetch data from Firestore
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [pastaTypesData, saucesData, drinksData] = await Promise.all([
                    fetchPastaTypes(),
                    fetchSauces(),
                    fetchDrinks()
                ]);

                setPastaTypes(pastaTypesData);
                setSauces(saucesData);
                setDrinks(drinksData);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching data:', error);
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const selectedPasta = pastaTypes.find(p => p.id === selectedPastaId);
    const step = selectedPasta?.unit ?? 0;

    const price = usePriceCalculator(selectedPasta, quantity, selectedSauceId, selectedDrinkId, sauces, drinks);

    // Track configuration changes
    useEffect(() => {
        // Only track if at least pasta type is selected
        if (selectedPastaId) {
            trackConfigurationChange('Pasta Configurator', {
                pastaType: selectedPasta ? t(`${selectedPasta.translationKey}.label`) : 'None',
                quantity,
                sauce: selectedSauceId ? t(`${sauces.find(s => s.id === selectedSauceId)?.translationKey}.label`) : 'None',
                drink: selectedDrinkId ? t(`${drinks.find(d => d.id === selectedDrinkId)?.translationKey}.label`) : 'None',
                totalPrice: price.total
            });
        }
    }, [selectedPastaId, quantity, selectedSauceId, selectedDrinkId, price.total, t, selectedPasta, sauces, drinks]);

    if (loading) {
        return (
            <section id="simulatore" className="py-16 bg-gray-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-2 text-center">{t('simulator.title')}</h2>
                    <p className="text-gray-600 mb-8 text-center">{t('simulator.description')}</p>
                    <div className="bg-white rounded-lg p-8 shadow-sm flex justify-center">
                        <p>Caricamento...</p>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section id="simulatore" className="py-16 bg-gray-50">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-2 text-center">{t('simulator.title')}</h2>
                <p className="text-gray-600 mb-8 text-center">{t('simulator.description')}</p>

                <div className="bg-white rounded-lg p-8 shadow-sm">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                            <label
                                className="block text-sm font-medium text-gray-700 mb-2">{t('simulator.pastaType')}</label>
                            <div className="relative">
                                <select
                                    value={selectedPastaId}
                                    onChange={(e) => setSelectedPastaId(e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-pastita-orange focus:border-transparent"
                                >
                                    <option value="">{t('simulator.choosePasta')}</option>
                                    {pastaTypes.map((pasta) => (
                                        <option key={pasta.id} value={pasta.id}>
                                            {t(`${pasta.translationKey}.label`)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div>
                            <label
                                className="block text-sm font-medium text-gray-700 mb-2">{t('simulator.sauce')}</label>
                            <div className="relative">
                                <select
                                    value={selectedSauceId}
                                    onChange={(e) => setSelectedSauceId(e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-pastita-orange focus:border-transparent"
                                >
                                    <option value="">{t('simulator.chooseSauce')}</option>
                                    {sauces.map((sauce) => (
                                        <option key={sauce.id} value={sauce.id}>
                                            {t(`${sauce.translationKey}.label`)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {selectedPasta && <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('simulator.quantity', {step})}
                            </label>

                            <div className="relative w-full">
                                <input
                                    type="range"
                                    min={step}
                                    max={1000}
                                    step={step}
                                    value={quantity}
                                    onChange={(e) => setQuantity(parseInt(e.target.value))}
                                    className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-gradient-to-r from-pastita-orange to-gray-200"
                                />
                            </div>

                            <div className="flex justify-between text-xs text-gray-500 mt-2">
                                <span>{step}g</span>
                                <span>1000g</span>
                            </div>

                            <p className="text-sm mt-2 text-center text-pastita-dark font-medium">
                                {t('simulator.selectedQuantity', {quantity})}
                            </p>
                        </div>
                        }

                        <div>
                            <label
                                className="block text-sm font-medium text-gray-700 mb-2">{t('simulator.drink')}</label>
                            <div className="relative">
                                <select
                                    value={selectedDrinkId}
                                    onChange={(e) => setSelectedDrinkId(e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-pastita-orange focus:border-transparent"
                                >
                                    <option value="">{t('simulator.addDrink')}</option>
                                    {drinks.map((drink) => (
                                        <option key={drink.id} value={drink.id}>
                                            {t(`${drink.translationKey}.label`)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="mt-8 p-4 sm:p-6 bg-orange-50 rounded-lg border-2 border-orange-200">
                        <h3 className="text-base sm:text-lg font-semibold text-pastita-orange mb-4 flex items-center">💰
                            {t('simulator.priceCalculation')}</h3>
                        <div className="space-y-2">
                            {selectedPasta && (
                                <div className="flex flex-wrap justify-between items-center">
                                    <span className="text-sm sm:text-base text-gray-700">
                                        {t(`${selectedPasta.translationKey}.label`)} ({quantity}g):
                                    </span>
                                    <span className="font-semibold">€{price.base?.toFixed(2)}</span>
                                </div>
                            )}
                            {selectedSauceId && (
                                <div className="flex flex-wrap justify-between items-center">
                                    <span className="text-sm sm:text-base text-gray-700">
                                        + ({t(`${sauces.find(s => s.id === selectedSauceId)?.translationKey}.label`)}):
                                    </span>
                                    <span className="font-semibold">€{price.sauce?.toFixed(2)}</span>
                                </div>
                            )}
                            {selectedDrinkId && (
                                <div className="flex flex-wrap justify-between items-center">
                                    <span className="text-sm sm:text-base text-gray-700">
                                        {t('menu.drinks.title')} ({t(`${drinks.find(d => d.id === selectedDrinkId)?.translationKey}.label`)}):
                                    </span>
                                    <span className="font-semibold">€{price.drink?.toFixed(2)}</span>
                                </div>
                            )}
                            <hr className="border-orange-200"/>
                            <div
                                className="flex justify-between items-center text-base sm:text-lg font-bold text-pastita-orange">
                                <span>{t('simulator.total')}</span>
                                <span>€{price.total?.toFixed(2)}</span>
                            </div>
                        </div>
                        {(!selectedPasta || !selectedSauceId) && (
                            <p className="text-xs sm:text-sm text-orange-600 mt-3">
                                {t('simulator.warning')}
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Configurator;
